import type { IOrderComponentDescriptor } from '@moko/utils';
import { internalFetch } from '../utils/fetchHelper';

export type CardSlotDisplay = 'block' | 'inline';
export type CardSlotAlign = 'left' | 'center' | 'right';
export type CardSlotFontSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ICardSlotStyle {
  display?: CardSlotDisplay;
  align?: CardSlotAlign;
  fontSize?: CardSlotFontSize;
  color?: string;
}

export interface ICardSlot {
  id: string;
  label: string;
  order: number;
  style?: ICardSlotStyle;
}

export type CardStaticFieldKey =
  | 'id'
  | 'code'
  | 'name'
  | 'description'
  | 'status'
  | 'entityTypeName';
export type CardStaticFieldPosition = 'start' | 'end';

export interface ICardStaticFieldBinding {
  key: CardStaticFieldKey;
  slotId: string;
  position: CardStaticFieldPosition;
}

export interface ICardLayoutDefinition {
  slots: ICardSlot[];
  staticFields?: ICardStaticFieldBinding[];
}

export interface IField {
  repeatingMode?: string;
  name: string;
  fieldType: string;
  componentCode?: string;
  options?: any;
  label: string;
  hint?: string;
  readonly?: boolean;
  readonlyForContractors?: boolean;
  tabName?: string;
  lookupList?: string;
  multiSelect?: boolean;
  hiden?: boolean;
  allowAppend?: boolean;
  reference?: string;
  dependees?: string;
  mandatory?: boolean;
  showInGrid?: boolean;
  filterable?: boolean;
  showInDefaultFilters?: boolean;
  showInClient?: boolean;
  showInReport?: boolean;
  cardSlotId?: string;
  cardSlotOrder?: number;
  optionsLayout?: string;
  labelAbove?: boolean;
  fullWidth?: boolean;
  copyToOrderedEntity?: boolean;
  permissions?: string[];
  rows?: number;
  effect?: string; // "Show" | "Hide"
  parentName?: string;
  isCollapsed?: boolean;
}

export interface ILookupValue {
  key: string;
  value: string;
  header?: boolean;
}

export interface ILookup {
  name: string;
  values: ILookupValue[];
}

export interface IValidationRule {
  rule: string;
  errorMessage: string;
}

export interface IValidation {
  field: string;
  rules: IValidationRule[];
}

export interface IFieldsTab {
  order: number;
  tabName: string;
  caption: string;
  hidden?: boolean;
}

export interface IEffect {
  fields: string[];
  effectType: string; // "Show" | "Hide"
}

export interface IRule {
  field: string;
  operator: string;
  value: string;
}

export interface ICondition {
  combinator: string; // "And" | "Or"
  not: boolean;
  rules: IRule[];
}

export interface IInteraction {
  name: string;
  description: string;
  condition: ICondition;
  effects: IEffect[];
}

export interface IOrderOptions {
  maximumAssetCount?: number | null;
  additionalAssetPricingItemCode?: string | null;
  proximity?: {
    enabled: boolean;
    maximumDistanceMiles?: number | null;
    measurement?: 'StraightLine' | 'Driving' | null;
    comparison?: 'AllPairs' | 'FromPrimary' | null;
  } | null;
}

export interface IMapOptions {
  coordinateField?: string;
  markerField?: string;
}

export interface IMetadata {
  billingActuals?: {
    fields: (
      | 'anchorCount'
      | 'waitHours'
      | 'equipmentHours'
      | 'propertyHours'
      | 'travelHours'
      | 'visit'
      | 'expenses'
      | 'returnTripCause'
    )[];
    quantitySources?: Record<string, string>;
    purchaseCapField?: string | null;
    purchaseCurrencyField?: string | null;
  } | null;
  id?: number;
  formDefinitionId?: number;
  status?: string;
  statusChangedAt?: string;
  statusChangedBy?: number;
  lookups: ILookup[];
  fields: IField[];
  validations: IValidation[];
  tabs?: IFieldsTab[];
  interactions: IInteraction[];
  cardLayout?: ICardLayoutDefinition;
  mapOptions?: IMapOptions;
  orderOptions?: IOrderOptions | null;
}

export interface IEntityMetadata {
  metadataId?: number;
  formDefinitionId?: number;
  status?: string;
  statusChangedAt?: string;
  statusChangedBy?: number;
  metadata?: IMetadata;
  lookups?: ILookup[];
  fields?: IField[];
  validations?: IValidation[];
  tabs?: IFieldsTab[];
  interactions?: IInteraction[];
}

export interface IUpdateMetadataResponse {
  version: number;
}

export interface IEntitiesMetadatasResponse {
  metadatas: Record<string, IEntityMetadata>;
}

export interface IArchivedMetadatasResponse {
  metadatas: Record<string, string>;
}

export interface IUpdateMetadataStatusRequest {
  status: string;
  action?: string;
  comment?: string;
}

export interface IUpdateMetadataStatusResponse {
  metadataId: number;
  status: string;
}

export const getMetadata = async (
  abortController: AbortController,
  entityType?: string,
  allFields?: boolean,
  name?: string,
  tenantId?: number
): Promise<IMetadata> => {
  const qs = new URLSearchParams({
    entity: entityType ?? '',
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  if (allFields) qs.set('allFields', 'true');
  if (name) qs.set('name', name);
  if (tenantId != null) qs.set('tenantId', String(tenantId));
  const metadata = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/metadata?${qs}`,
    'GET',
    abortController
  );

  return normalizeMetadataForApi(
    metadata as IMetadata | IEntityMetadata | null | undefined
  );
};

export const getSpecificMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  version: number,
  allFields = true,
  tenantId?: number
): Promise<IMetadata> => {
  const qs = new URLSearchParams({
    entityType,
    name,
    version: String(version),
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  if (allFields) qs.set('allFields', 'true');
  if (tenantId != null) qs.set('tenantId', String(tenantId));
  const metadata = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/specificMetadata?${qs}`,
    'GET',
    abortController
  );
  return normalizeMetadataForApi(
    metadata as IMetadata | IEntityMetadata | null | undefined
  );
};

export const revertMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  version: number,
  metadata: IMetadata
): Promise<IUpdateMetadataResponse> => {
  const normalized = normalizeMetadataForApi(metadata);
  const qs = new URLSearchParams({
    entityType,
    name,
    version: String(version),
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/revertMetadata?${qs}`,
    'POST',
    abortController,
    JSON.stringify({ metadata: normalized })
  );
};

export const getEntitiesMetadatas = async (
  abortController: AbortController,
  entityTypes?: string[],
  tenantId?: number
): Promise<IEntitiesMetadatasResponse> => {
  const qs = new URLSearchParams();
  if (entityTypes && entityTypes.length > 0) {
    qs.set('entityTypes', entityTypes.join(','));
  }
  if (tenantId != null) qs.set('tenantId', String(tenantId));
  qs.set('code', import.meta.env.VITE_BACKEND_API_KEY);
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/latestMetadatas?${qs.toString()}`,
    'GET',
    abortController
  );
};

export type { IOrderComponentDescriptor } from '@moko/utils';

export interface IOrderWorkFormRelationship {
  tenantId: number;
  orderFormDefinitionId: number;
  orderFormCode: string;
  orderFormLabel: string;
  workFormDefinitionId: number;
  workFormCode: string;
  workFormLabel: string;
  publishedMetadataId: number | null;
  publishedVersion: number | null;
  publishedMetadataName: string | null;
}

const formRelationshipQuery = (
  orderFormDefinitionId: number,
  tenantId: number
) =>
  new URLSearchParams({
    orderFormDefinitionId: String(orderFormDefinitionId),
    tenantId: String(tenantId),
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });

export const getOrderComponentCatalog = async (
  abortController: AbortController
): Promise<IOrderComponentDescriptor[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/orderComponentCatalog?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getOrderWorkFormRelationships = async (
  abortController: AbortController,
  orderFormDefinitionId: number,
  tenantId: number
): Promise<IOrderWorkFormRelationship[]> => {
  const qs = formRelationshipQuery(orderFormDefinitionId, tenantId);
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadata/orderWorkFormRelationships?${qs}`,
    'GET',
    abortController
  );
};

export const createOrderWorkFormRelationship = async (
  abortController: AbortController,
  orderFormDefinitionId: number,
  workFormDefinitionId: number,
  tenantId: number
): Promise<IOrderWorkFormRelationship> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadata/orderWorkFormRelationships?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'POST',
    abortController,
    JSON.stringify({ orderFormDefinitionId, workFormDefinitionId, tenantId })
  );
};

export const deleteOrderWorkFormRelationship = async (
  abortController: AbortController,
  orderFormDefinitionId: number,
  workFormDefinitionId: number,
  tenantId: number
): Promise<void> => {
  const qs = formRelationshipQuery(orderFormDefinitionId, tenantId);
  qs.set('workFormDefinitionId', String(workFormDefinitionId));
  await internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadata/orderWorkFormRelationships?${qs}`,
    'DELETE',
    abortController
  );
};

export interface IMetadataListItem {
  id: number;
  name: string;
  entity: string;
  version: number;
  createdAt: string;
  status: string;
  statusChangedBy: number | null;
  statusDate: string;
  comment: string | null;
}

export interface IMetadatasPagedResponse {
  isSuccess: boolean;
  message: string | null;
  correlationId: string;
  items: IMetadataListItem[];
  // Optional — BE may add later. Consumers should fall back gracefully.
  pageCount?: number;
  isLastPage?: boolean;
  rowsCount?: number;
}

export const getMetadatasPaged = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IMetadatasPagedResponse> => {
  const qs = new URLSearchParams();
  qs.set('pageSize', String(pageSize));
  qs.set('pageNo', String(pageNo));
  if (orderBy) qs.set('orderBy', orderBy);
  if (search) qs.set('search', search);
  if (filters) qs.set('filters', filters);
  qs.set('code', import.meta.env.VITE_BACKEND_API_KEY);
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/metadatas?${qs.toString()}`,
    'GET',
    abortController
  );
};
export const getLatestMetadatasNames = async (
  abortController: AbortController,
  entityTypes?: string[],
  tenantId?: number,
  isPublished = false
): Promise<string[]> => {
  const qs = new URLSearchParams({
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  if (entityTypes) qs.set('entityTypes', entityTypes.join(','));
  if (isPublished) qs.set('isPublished', 'true');
  if (tenantId != null) qs.set('tenantId', String(tenantId));
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/latestMetadatasNames?${qs}`,
    'GET',
    abortController
  );
};

export interface IPublishedFormMetadata {
  name: string;
  metadata: IMetadata;
}

export const getPublishedFormMetadatas = async (
  abortController: AbortController,
  entityType: string,
  tenantId: number
): Promise<IPublishedFormMetadata[]> => {
  const formNames = await getLatestMetadatasNames(
    abortController,
    [entityType],
    tenantId,
    true
  );
  const names = formNames
    .map((key) => {
      const separator = key.indexOf('|');
      return separator >= 0 ? key.slice(separator + 1).trim() : key.trim();
    })
    .filter(Boolean);

  const forms = await Promise.all(
    names.map(async (name) => ({
      name,
      metadata: await getMetadata(
        abortController,
        entityType,
        true,
        name,
        tenantId
      ),
    }))
  );
  const missingIdentity = forms.filter(
    (form) =>
      !Number.isInteger(form.metadata.formDefinitionId) ||
      (form.metadata.formDefinitionId ?? 0) <= 0
  );
  if (missingIdentity.length > 0) {
    throw new Error(
      `Published ${entityType} form identity could not be loaded for tenant ${tenantId}: ${missingIdentity
        .map(
          (form) =>
            `${form.name} (metadata ID ${form.metadata.id ?? 'missing'})`
        )
        .join(
          ', '
        )}. The API response must include a valid formDefinitionId. Verify the selected tenant and running API version.`
    );
  }
  return forms;
};
export const getArchivedMetadatas = async (
  abortController: AbortController
): Promise<IArchivedMetadatasResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/archivedMetadatas?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

function createEmptyMetadata(): IMetadata {
  return {
    lookups: [],
    fields: [],
    validations: [],
    interactions: [],
  };
}

function normalizeValidationRules(rules: unknown): IValidationRule[] {
  if (!Array.isArray(rules)) {
    return [];
  }

  return rules
    .map((rule): IValidationRule | null => {
      if (typeof rule === 'string') {
        return { rule, errorMessage: '' };
      }

      if (rule && typeof rule === 'object') {
        const parsedRule =
          typeof (rule as { rule?: unknown }).rule === 'string'
            ? (rule as { rule: string }).rule
            : '';
        const parsedErrorMessage =
          typeof (rule as { errorMessage?: unknown }).errorMessage === 'string'
            ? (rule as { errorMessage: string }).errorMessage
            : '';

        if (!parsedRule) {
          return null;
        }

        return {
          rule: parsedRule,
          errorMessage: parsedErrorMessage,
        };
      }

      return null;
    })
    .filter((rule): rule is IValidationRule => rule !== null);
}

function normalizeValidations(validations: unknown): IValidation[] {
  if (!Array.isArray(validations)) {
    return [];
  }

  return validations
    .map((validation): IValidation | null => {
      if (!validation || typeof validation !== 'object') {
        return null;
      }

      const field =
        typeof (validation as { field?: unknown }).field === 'string'
          ? (validation as { field: string }).field
          : '';

      if (!field) {
        return null;
      }

      return {
        field,
        rules: normalizeValidationRules(
          (validation as { rules?: unknown }).rules ?? []
        ),
      };
    })
    .filter((validation): validation is IValidation => validation !== null);
}

function normalizeMetadataForApi(
  metadata?: IMetadata | IEntityMetadata | null
): IMetadata {
  if (!metadata || typeof metadata !== 'object') {
    return createEmptyMetadata();
  }

  const entityMetadata = metadata as IEntityMetadata;
  const normalizedInput =
    entityMetadata.metadata && typeof entityMetadata.metadata === 'object'
      ? entityMetadata.metadata
      : (metadata as IMetadata);

  const legacyMetadata = normalizedInput as IMetadata & { slots?: ICardSlot[] };
  const { slots, ...rest } = legacyMetadata;

  // Older API responses use the CLR property name. Keep one editor key so
  // spreading config during edits cannot create case-insensitive duplicates.
  const actuals = rest.billingActuals as
    | (NonNullable<IMetadata['billingActuals']> & {
        QuantitySources?: Record<string, string>;
      })
    | null
    | undefined;
  const normalizedActuals = actuals
    ? (() => {
        const { QuantitySources, ...config } = actuals;
        return {
          ...config,
          quantitySources: config.quantitySources ?? QuantitySources ?? {},
        };
      })()
    : actuals;

  const normalizedRest: IMetadata = {
    ...rest,
    billingActuals: normalizedActuals,
    lookups: rest.lookups ?? [],
    fields: rest.fields ?? [],
    validations: normalizeValidations(rest.validations),
    interactions: rest.interactions ?? [],
  };

  if (normalizedRest.cardLayout?.slots?.length) {
    return normalizedRest;
  }

  if (Array.isArray(slots) && slots.length > 0) {
    return {
      ...normalizedRest,
      cardLayout: {
        slots,
      },
    };
  }

  return normalizedRest;
}

export interface IImportMetadataResponse {
  version: number;
  status: string;
  referenceValuesCreatedCount: number;
  referenceValuesUpdatedCount: number;
}

const problemMessage = (body: unknown) => {
  if (!body || typeof body !== 'object') return 'Import failed.';
  const problem = body as {
    title?: string;
    detail?: string;
    message?: string;
    Message?: string;
    errors?: Record<string, string | string[]>;
  };
  const lines: string[] = [];
  if (problem.detail) lines.push(problem.detail);
  else if (problem.title) lines.push(problem.title);
  else if (problem.message || problem.Message) {
    lines.push(problem.message ?? problem.Message ?? '');
  }
  if (problem.errors && typeof problem.errors === 'object') {
    Object.entries(problem.errors).forEach(([key, value]) => {
      const text = Array.isArray(value) ? value.join(' ') : String(value);
      lines.push(key ? `${key}: ${text}` : text);
    });
  }
  return lines.filter(Boolean).join(' ') || 'Import failed.';
};

export const exportMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  version: number
): Promise<Response> => {
  const qs = new URLSearchParams({
    entityType,
    name,
    version: String(version),
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/exportMetadata?${qs}`,
    'GET',
    abortController,
    undefined,
    true
  );
};

export const importMetadata = async (
  abortController: AbortController,
  pkg: unknown,
  name?: string
): Promise<IImportMetadataResponse> => {
  const qs = new URLSearchParams({
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  const trimmedName = name?.trim();
  if (trimmedName) qs.set('name', trimmedName);
  const body = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/importMetadata?${qs}`,
    'POST',
    abortController,
    JSON.stringify(pkg),
    undefined,
    undefined,
    [400, 422]
  );
  if (
    body &&
    typeof body === 'object' &&
    typeof body.version === 'number' &&
    typeof body.referenceValuesCreatedCount === 'number'
  ) {
    return body as IImportMetadataResponse;
  }
  throw new Error(problemMessage(body));
};

export const updateMetadata = async (
  abortController: AbortController,
  entityType: string,
  metadata: IMetadata,
  name?: string
): Promise<IUpdateMetadataResponse> => {
  const normalizedMetadata = normalizeMetadataForApi(metadata);

  const actuals = normalizedMetadata.billingActuals;
  // Match the persisted .NET contract used by default metadata deserialization.
  const apiMetadata = actuals
    ? (() => {
        const { quantitySources, ...config } = actuals;
        return {
          ...normalizedMetadata,
          billingActuals: { ...config, QuantitySources: quantitySources ?? {} },
        };
      })()
    : normalizedMetadata;

  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadata?entityType=${entityType}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify({
      name: name,
      metadata: apiMetadata,
    })
  );
};

export const updateMetadataStatus = async (
  abortController: AbortController,
  metadataId: number,
  request: IUpdateMetadataStatusRequest
): Promise<IUpdateMetadataStatusResponse> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadataStatus?metadataId=${metadataId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify(request)
  );
};

export interface IValidateMetadataResponse {
  errors: Record<string, string> | null;
  isSuccess: boolean;
  message: string | null;
  correlationId?: string;
}

export const validateMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  metadata: IMetadata
): Promise<IValidateMetadataResponse> => {
  const normalizedMetadata = normalizeMetadataForApi(metadata);

  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/validateMetadata?entityType=${encodeURIComponent(
      entityType
    )}&name=${encodeURIComponent(name)}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'POST',
    abortController,
    JSON.stringify({ metadata: normalizedMetadata }),
    undefined,
    undefined,
    [422]
  );
};

export interface IMetadataFieldEntities {
  field: string;
  entities: number[];
}

export interface IMetadataEntitiesResponse {
  fieldEntityIds: IMetadataFieldEntities[];
  isSuccess: boolean;
  message: string | null;
  correlationId?: string;
}

export const getMetadataEntities = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  fields: string[]
): Promise<IMetadataEntitiesResponse> => {
  const fieldsParam = fields.join(',');
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadataEntities?entityType=${encodeURIComponent(
      entityType
    )}&name=${encodeURIComponent(name)}&fields=${encodeURIComponent(
      fieldsParam
    )}&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};
